import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Context,
  type ReactNode,
} from 'react';
import type {
  AuthContextValue,
  AuthSession,
  AuthState,
  AuthUser,
  MagicLinkProviderConfig,
  SignInInput,
} from './types';

interface Props {
  config: MagicLinkProviderConfig;
  context: Context<AuthContextValue | null>;
  children: ReactNode;
}

// Bridges Supabase Auth's session shape to our provider-agnostic AuthUser /
// AuthSession. Supabase issues a JWT with sub = auth.users.id (uuid). The
// public.users row is keyed on supabase_user_id (created by the after-insert
// trigger in 20260501130000_supabase_auth.sql).
//
// Note: AuthUser.id reflects auth.users.id here, not public.users.id. The
// worker does the auth.users → public.users lookup on every request and
// attaches the public id to its UserCtx. The frontend doesn't need the
// public id directly — every fetch goes through the worker.
function toAuthUser(session: Session): AuthUser {
  const meta = session.user.user_metadata ?? {};
  const displayName =
    (typeof meta.name === 'string' && meta.name) ||
    (typeof meta.full_name === 'string' && meta.full_name) ||
    session.user.email?.split('@')[0] ||
    session.user.id;
  return {
    id: session.user.id,
    email: session.user.email ?? '',
    displayName,
    supabaseUserId: session.user.id,
  };
}

function toAuthSession(s: Session): AuthSession {
  return {
    user: toAuthUser(s),
    accessToken: s.access_token,
    // expires_at is unix seconds. Convert to ms; default to "now" if missing
    // (shouldn't happen in practice).
    expiresAt: s.expires_at ? s.expires_at * 1000 : Date.now(),
  };
}

export function MagicLinkProvider({ config, context: AuthContext, children }: Props) {
  const clientRef = useRef<SupabaseClient | null>(null);
  if (!clientRef.current) {
    clientRef.current = createClient(config.supabaseUrl, config.supabaseAnonKey, {
      auth: {
        // Supabase persists the session in localStorage and auto-refreshes
        // before expiry. We piggyback on that — no manual refresh code here.
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    });
  }
  const client = clientRef.current;

  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void client.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setState(
        data.session
          ? { status: 'signed_in', session: toAuthSession(data.session) }
          : { status: 'signed_out' },
      );
    });
    const { data: sub } = client.auth.onAuthStateChange((_event, session) => {
      setState(
        session
          ? { status: 'signed_in', session: toAuthSession(session) }
          : { status: 'signed_out' },
      );
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [client]);

  const signIn = useCallback(
    async (input: SignInInput) => {
      if (input.kind !== 'magic_link') {
        throw new Error('MagicLinkProvider only supports kind: "magic_link" sign-in.');
      }
      const email = input.email.trim().toLowerCase();
      // Frontend-side guard so users get a fast error before the round-trip.
      // The DB trigger (enforce_zanda_signup) is the authoritative check.
      if (!email.endsWith(`@${config.allowedDomain}`)) {
        throw new Error(`Sign-in restricted to @${config.allowedDomain} addresses.`);
      }
      const { error } = await client.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: config.redirectUri,
          // shouldCreateUser stays default (true) — the DB trigger blocks
          // non-zanda emails. Existing users land on the same flow.
        },
      });
      if (error) throw error;
    },
    [client, config.allowedDomain, config.redirectUri],
  );

  const signOut = useCallback(async () => {
    const { error } = await client.auth.signOut();
    if (error) throw error;
  }, [client]);

  const getAccessToken = useCallback(async () => {
    const { data } = await client.auth.getSession();
    return data.session?.access_token ?? null;
  }, [client]);

  const value = useMemo<AuthContextValue>(
    () => ({ state, signIn, signOut, getAccessToken, realtimeClient: client }),
    [state, signIn, signOut, getAccessToken, client],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
